import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Select } from '@ngxs/store';
import { ModulesSelectors } from '@serviceos-ng/core';
import { ScriptService } from 'ngx-script-loader';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';
import {
    mapCardBrand,
    stripNonAlphanumericChars,
} from '../../../helpers/helper-functions';

@Component({
    selector: 'serviceos-web-stripe',
    templateUrl: './stripe.component.html',
    styleUrls: ['./stripe.component.scss'],
})
export class StripeComponent implements OnInit {
    @Input()
    public paymethodSetup: any;

    private dependenciesScripts = {
        v1: 'https://js.stripe.com/v1/',
        v3: 'https://js.stripe.com/v3/',
    };

    public failToLoadDependence = false;
    private window = window as any;
    private stripe;
    public stripeVersion = 'v3'; // Change it to 'v1' for backup
    // public cardDetailsForm: FormGroup;
    public unsupportedBrands: Array<string> = [];
    private unsupportedBrandUsed = false;

    private style = {
        base: {
            color: '#32325d',
            fontWeight: 600,
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            ':focus': {
                color: '#424770',
            },

            '::placeholder': {
                color: 'transparent',
            },

            ':focus::placeholder': {
                color: 'transparent',
            },
        },
        invalid: {
            color: '#a94442',
            iconColor: '#a94442',
        },
    };

    private elementClasses = {
        focus: 'sos-focused',
        empty: 'empty',
        invalid: 'invalid',
        complete: 'complete',
    };
    private paymentDepSubscription;
    private elementDestroyed = false;
    private cardElements;
    public formLoaded = false;

    @Select(ModulesSelectors.getComponentFromModule('customer', 'payments'))
    paymentsConfig$!: Observable<any>

    public paymentsConfig: any = null;

    constructor(private _scriptService: ScriptService) {
        this.paymentsConfig$.subscribe((config) => {
            if (config) {
                this.paymentsConfig = config;
            }
        });
    }

    ngOnInit(): void {
        this.initialize();
    }

    /**
     * Initialize component
     */
    private initialize() {
        // get obf options
        // const loader = this.loaderService.showLoader();

        this.paymentDepSubscription = this._scriptService
            .loadScript(
                this.dependenciesScripts[this.stripeVersion],
                { async: 'true' },
                'body'
            )
            .pipe(
                retry(1) // ! DISABLE FOR TESTING THE subscription error
            )
            .subscribe(
                (load) => {
                    // this.loaderService.hideLoader(loader);
                    // this.failToLoadDependencies.emit(false);

                    if (this.paymethodSetup) {
                        let publicKey = null;

                        // Get payment method public key
                        if (
                            this.paymethodSetup.payload &&
                            this.paymethodSetup.payload.public_key
                        ) {
                            publicKey = this.paymethodSetup.payload.public_key;
                        }

                        // // Get unsupported brands for this payment method
                        if (
                            this.paymethodSetup.payload &&
                            this.paymethodSetup.payload.unsupported_brands
                        ) {
                            this.unsupportedBrands = this.paymethodSetup.payload.unsupported_brands;
                        }

                        if (this.stripeVersion === 'v1') {
                            // /**
                            //  * Stripe v1
                            //  */
                            // this.stripe = this.window.Stripe;
                            // this.stripe.setPublishableKey(this.publicKey);
                            // this.cardDetailsForm = this.fb.group(
                            //     {
                            //         cardNumber: ['', Validators.required],
                            //         expiry: ['', Validators.required],
                            //         nameOnCard: ['', Validators.required],
                            //         cvv: ['', Validators.required],
                            //     },
                            //     { validator: PaymethodFormValidator }
                            // );
                        } else if (this.stripeVersion === 'v3') {
                            // Init Stripe v3 form using Elements API
                            if (!this.stripe) {
                                this.stripe = this.window.Stripe(publicKey);
                            }

                            // if (this.isNewPayment && !this.paymethod) {
                            // dont REinitialize form when there's new paymethod setup because of an expired valid_to
                            // if (!this.formLoaded) {
                            this.cardInit();
                            // }
                            // } else {
                            //     this.formLoaded = false;
                            // }
                        }

                        // this.initializationSubscription.next(true);
                    }
                },
                (error) => {
                    // this.loaderService.hideLoader(loader);
                    if (this.stripeVersion === 'v3') {
                        // this.failToLoadDependencies.emit(true); reject!
                        this.failToLoadDependence = true;
                    } else {
                        this.stripeVersion = 'v3';
                        this.initialize();
                    }
                }
            );
    }

    /**
     * Init Stripe Elements API for Stripe v3
     */
    private cardInit() {
        setTimeout(() => {
            if (!this.elementDestroyed) {
                this.createElements();
                this.errorHandler();
            }
        }, 1);
    }

    /**
     * Mount input fields for Elements API (Stripe v3)
     */
    private createElements(): void {
        const elements = this.stripe.elements(); // create Elements instance

        const cardNumber = elements.create('cardNumber', {
                style: this.style,
                classes: this.elementClasses,
            }),
            cardExpiry = elements.create('cardExpiry', {
                style: this.style,
                classes: this.elementClasses,
            }),
            cardCvc = elements.create('cardCvc', {
                style: this.style,
                classes: this.elementClasses,
            });

        cardNumber.mount('#card-number');
        cardExpiry.mount('#card-expiry');
        cardCvc.mount('#card-cvc');

        this.cardElements = [cardNumber, cardExpiry, cardCvc];

        this.formLoaded = true;

        // Handle real-time validation errors from the card Element.
        cardNumber.on('change', (event) => {
            if (event && event.complete && !event.error && event.brand) {
                const mappedCardBrandName: string | null = mapCardBrand(
                        event.brand
                    ),
                    cardBrandName: string = mappedCardBrandName
                        ? mappedCardBrandName
                        : stripNonAlphanumericChars(event.brand).toLowerCase();

                // Set flag which will open modal for unsupported card brand
                this.unsupportedBrandUsed =
                    this.unsupportedBrands &&
                    this.unsupportedBrands.find(
                        (brandName: string) =>
                            stripNonAlphanumericChars(
                                brandName
                            ).toLowerCase() === cardBrandName
                    )
                        ? true
                        : false;
            }
        });
    }

    /**
     * Register event lisseners for errors and populate the errors messages
     */
    private errorHandler() {
        const cardError = document.querySelector('#card-errors'),
            savedErrors = {};

        if (cardError) {
            this.cardElements.forEach((element, idx) => {
                element.on('change', (event) => {
                    if (event.error) {
                        savedErrors[idx] = event.error.message;
                        cardError.innerHTML = event.error.message;
                    } else {
                        savedErrors[idx] = null;

                        // Loop over the saved errors and find the first one, if any.
                        const nextError = Object.keys(savedErrors)
                            .sort()
                            .reduce((maybeFoundError, key) => {
                                return maybeFoundError || savedErrors[key];
                            }, null);

                        if (nextError) {
                            // Now that they've fixed the current error, show another one.
                            cardError.innerHTML = nextError;
                        } else {
                            // The user fixed the last error; no more errors.
                            cardError.innerHTML = '';
                        }
                    }
                });
            });
        }
    }

    /**
     * Remove iframes, created by Stripe.js library
     */
    private removeStripeIframes(): void {
        // Remove Stripe controller iframes
        if (this.stripe._controller) {
            let controllerIframeName = this.stripe._controller._controllerFrame
                ._controllerId;

            if (controllerIframeName) {
                // Remove id (number) from string
                controllerIframeName = controllerIframeName.replace(
                    /[0-9]/g,
                    ''
                );

                const stripeIframes: any = document.querySelectorAll(
                    '[name*=' + controllerIframeName
                );
                for (let index = 0; index < stripeIframes.length; index++) {
                    const stripeIframe = stripeIframes[index];
                    stripeIframe.remove();
                }
            }
        }

        // Remove Stripe Metrics iframes
        if (this.stripe._ec) {
            let metricsIframeName = this.stripe._ec._id;

            if (metricsIframeName) {
                // Remove id (number) from string
                metricsIframeName = metricsIframeName.replace(/[0-9]/g, '');

                const stripeIframes: any = document.querySelectorAll(
                    '[name*=' + metricsIframeName
                );

                for (let index = 0; index < stripeIframes.length; index++) {
                    const stripeIframe = stripeIframes[index];
                    stripeIframe.remove();
                }
            }
        }
    }

    public ngOnDestroy() {
        // Remove stripe iframes
        try {
            this.removeStripeIframes();
        } catch (e) {
            // do noting if whe got error
        }

        this.elementDestroyed = true;
        if (
            typeof this.paymentDepSubscription !== 'undefined' &&
            this.paymentDepSubscription !== null
        ) {
            this.paymentDepSubscription.unsubscribe();
        }
    }

    /**
     * Make Token for the payment
     * To create token only need to pass one of the element.
     * The Element pulls data from other Elements you've created on the same instance of elements to tokenize.
     */
    public getPaymentObject(typeOfCard: 'new' | 'existing'): Promise<any> {
        return new Promise((resolve, reject) => {
            if (this.failToLoadDependence) {
                return reject({ message: 'Fail to load dependencies' });
            }

            if (this.unsupportedBrandUsed) {
                return reject({ message: 'Your card is not supported. Please, try a different card brand.'});
            }

            const stripeParameters: Array<any> = [
                this.paymethodSetup.payload.client_secret,
            ];

            // assign payment_method object if we're using new card
            if (typeOfCard === 'new') {
                stripeParameters.push({
                    payment_method: {
                        card: this.cardElements[0],
                    },
                });
            }

            const isCardSetup: boolean = this.paymethodSetup.type === 'stripe_setup_create';

            this.stripe[
                isCardSetup
                    ? 'confirmCardSetup'
                    : 'confirmCardPayment'
            ](...stripeParameters)
                .then(
                    (result) => {
                        if (result.error) {
                            reject({ message: result.error.message });
                        } else {
                            
                            resolve({
                                token: isCardSetup ? result.setupIntent.payment_method : result.paymentIntent.id
                            });
                        }
                    },
                    (response: any) => {
                        // Error
                        reject({ message: 'We have a technical issue with our payment system. It should be fixed soon, please try again later.' });
                    }
                )
                .catch((error) => {
                    reject({ message: 'We have a technical issue with our payment system. It should be fixed soon, please try again later.' });
                });
        });
    }
}
